// Platform-wide display timezone: US Pacific (美西时间).
//
// Every timestamp the app renders should read the same no matter where the
// viewer sits — a coach in Los Angeles and one in Shanghai must see the same
// wall-clock for the same appointment. Rather than rewrite the ~500 formatting
// call sites scattered across the app, this installs a default `timeZone` on
// `Date.prototype.toLocale*`, so any call that does NOT name a timezone renders
// in Pacific. Calls that already pass `timeZone` (the booking/invite flows) are
// left exactly as they were.
//
// Only Date.prototype is patched. `Number.prototype.toLocaleString` — used all
// over the billing pages for thousands separators — is a different function and
// is untouched. `Intl.DateTimeFormat` is also untouched, because several places
// probe `Intl.DateTimeFormat().resolvedOptions().timeZone` to detect where the
// viewer actually is (region detect, the China-restrictions check); forcing
// Pacific there would break that detection.

import { laInputToUTCISO, inputInTimezoneToUTCISO } from './laTime';

export const PACIFIC_TZ = 'America/Los_Angeles';

// 'PST' in winter, 'PDT' in summer — resolved for the given instant so the
// label is never an hour wrong across a DST boundary.
export function pacificAbbr(when = new Date()) {
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone: PACIFIC_TZ, timeZoneName: 'short' })
      .formatToParts(when).find(p => p.type === 'timeZoneName')?.value || 'PT';
  } catch {
    return 'PT';
  }
}

// Pure calendar dates (DOB, 签约日, a month-grid cell) must NOT be re-zoned:
// moving them to Pacific walks them back a day for anyone east of it, which is
// the exact off-by-one calendarDate.js exists to prevent. They show up in two
// shapes, and both land on midnight:
//   - `new Date('1990-05-02')`  → midnight UTC   (mysql2 serializes DATE this way)
//   - `new Date(1990, 4, 2)`    → midnight LOCAL (parseCalDate, calendar grids)
// A real timestamp lands on midnight only by coincidence, so midnight in either
// frame means "treat this as a date, leave it alone".
const isCalendarDate = (d) => (
  (d.getUTCHours() === 0 && d.getUTCMinutes() === 0 &&
   d.getUTCSeconds() === 0 && d.getUTCMilliseconds() === 0) ||
  (d.getHours() === 0 && d.getMinutes() === 0 &&
   d.getSeconds() === 0 && d.getMilliseconds() === 0)
);

// Does this call render a TIME of day? Only those get the PDT / PST label —
// a plain date ("Oct 1, 2026") with a timezone stuck on it reads as noise, and
// pure calendar dates are not re-zoned at all (see isCalendarDate).
const TIME_FIELDS = ['hour', 'minute', 'second', 'timeStyle', 'dayPeriod', 'fractionalSecondDigits'];
const DATE_FIELDS = ['year', 'month', 'day', 'weekday', 'dateStyle', 'era'];

function rendersTimeOfDay(name, options) {
  if (name === 'toLocaleDateString') return false;
  if (name === 'toLocaleTimeString') return true;
  // toLocaleString: with no field options the default output is date + time.
  if (!options) return true;
  if (TIME_FIELDS.some((k) => k in options)) return true;
  if (DATE_FIELDS.some((k) => k in options)) return false;
  return true;
}

export function installPacificTime() {
  if (Date.prototype.__pacificTimeInstalled) return;

  ['toLocaleString', 'toLocaleDateString', 'toLocaleTimeString'].forEach((name) => {
    const original = Date.prototype[name];
    if (typeof original !== 'function') return;
    Date.prototype[name] = function patchedToLocale(locales, options) {
      const explicitTz = options && options.timeZone;
      if (!explicitTz && !isNaN(this.getTime()) && !isCalendarDate(this)) {
        const patched = { ...(options || {}), timeZone: PACIFIC_TZ };
        const out = original.call(this, locales, patched);
        // ...and say so on screen: every rendered time carries PDT / PST, so a
        // viewer in Shanghai reads the same label as one in Los Angeles and
        // nobody has to guess whose clock a time belongs to. A call that names
        // its own timeZoneName keeps it.
        //
        // The label is appended rather than left to `timeZoneName: 'short'`,
        // because Intl renders that as "GMT-7" under zh-CN (and puts it in the
        // middle of the string); the ask is PDT / PST, in both languages.
        if (!('timeZoneName' in patched) && rendersTimeOfDay(name, options)) {
          return `${out} ${pacificAbbr(this)}`;
        }
        return out;
      }
      return original.call(this, locales, options);
    };
  });

  Object.defineProperty(Date.prototype, '__pacificTimeInstalled', {
    value: true, enumerable: false, writable: false, configurable: false,
  });
}

// ── For formatters that cannot be patched ──────────────────────────────────
// date-fns `format()` (and any hand-rolled getHours()/getMinutes() string)
// reads a Date's LOCAL components, i.e. the viewer's machine. Shifting the
// instant so its local components equal Pacific's makes those formatters print
// Pacific. The returned Date is for FORMATTING ONLY — its epoch value is
// deliberately wrong, so never send it back to the API or do arithmetic on it.
export function toPacificWallClock(value) {
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return d;
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', {
      timeZone: PACIFIC_TZ,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    }).formatToParts(d).map((x) => [x.type, x.value])
  );
  const hour = parts.hour === '24' ? '00' : parts.hour;
  return new Date(+parts.year, +parts.month - 1, +parts.day, +hour, +parts.minute, +parts.second);
}

// Does a date-fns pattern print a time of day? (h/H = hour, m = minute,
// s = second, a/b/B = day period — letters inside '' are literals.)
const TIME_TOKEN_RE = /(^|[^'])[hHmsaBb]/;

// date-fns `format()` rendered in Pacific, with the PDT / PST label appended
// whenever the pattern shows a time. Pass a plain calendar date (a DOB, a
// 签约日) straight to date-fns instead — those must not be re-zoned.
export function formatPacific(fmt, value, pattern, options) {
  if (value == null || value === '') return '';
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return '';
  const out = fmt(toPacificWallClock(d), pattern, options);
  return TIME_TOKEN_RE.test(pattern) ? `${out} ${pacificAbbr(d)}` : out;
}

// ── Wall-clock values ──────────────────────────────────────────────────────
// Appointment columns (calendar_events.start_datetime, bookings.start_at,
// dating_schedules.scheduled_date/time) hold a NAIVE datetime: the time a
// matchmaker typed, which this business always means in Pacific. mysql2 tags
// that naive value with the server's zone (UTC) on the way out, so the ISO the
// API sends carries the typed components in its UTC fields. Reading them with
// the UTC getters recovers exactly what was typed — anything that re-zones such
// a value (including the Date.prototype patch above) walks it off by 7-8 hours.
//
// Returns a Date whose LOCAL components equal the typed ones, for formatting
// only: never send it back to the API.
export function parseWallClock(value) {
  if (!value) return null;
  if (value instanceof Date) {
    return isNaN(value.getTime())
      ? null
      : new Date(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate(),
                 value.getUTCHours(), value.getUTCMinutes(), value.getUTCSeconds());
  }
  const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +(m[6] || 0));
  const d = new Date(value);
  if (isNaN(d.getTime())) return null;
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(),
                  d.getUTCHours(), d.getUTCMinutes(), d.getUTCSeconds());
}

// PDT or PST on the day of a wall-clock value (the abbreviation that applies to
// that appointment, not to today).
export function wallClockAbbr(value) {
  const d = parseWallClock(value);
  if (!d) return 'PT';
  try {
    const iso = laInputToUTCISO(
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
      + `T${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
    );
    return pacificAbbr(iso ? new Date(iso) : d);
  } catch {
    return 'PT';
  }
}

// Format a wall-clock appointment. `options` are Intl options; the Pacific label
// is appended whenever the format shows a time of day. The timeZone is pinned to
// UTC so the Date.prototype patch leaves these alone — the components are
// already the Pacific ones.
export function formatWallClock(value, locales, options = {}) {
  const d = parseWallClock(value);
  if (!d) return '';
  const shifted = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(),
                                    d.getHours(), d.getMinutes(), d.getSeconds()));
  const out = shifted.toLocaleString(locales, { ...options, timeZone: 'UTC' });
  const showsTime = TIME_FIELDS.some((k) => k in options) || !Object.keys(options).length;
  return showsTime ? `${out} ${wallClockAbbr(value)}` : out;
}

// ── Values stored with their own timezone ──────────────────────────────────
// A few features (the coach 预约 list) store a naive datetime PLUS the zone it
// was typed in, e.g. "2026-05-19 17:00" + "Asia/Shanghai". Reading them means
// resolving that pair into a real instant first; display then follows the rest
// of the app into Pacific.
export function zonedToInstant(value, timezone) {
  const d = parseWallClock(value);
  if (!d) return null;
  const naive =
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    + `T${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  try {
    const iso = inputInTimezoneToUTCISO(naive, timezone || PACIFIC_TZ);
    return iso ? new Date(iso) : d;
  } catch {
    return d;
  }
}
